import {useState, useEffect, useCallback, useRef} from 'react';
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import { buildUrl, formatForDisplay } from './utils';
import { useNavigate } from "react-router-dom";

function Registrations() {
    const [registrations, setRegistrations] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        title: "",
        first_name: "",
        surname: "",
        id_number: "",
        min_dob: "",
        max_dob: "",
        email: "",
        cell_number: "",
        wa_id: "",
        referral_code: "",
        status: "",
        min_date: "",
        max_date: ""
    });
    const [loading, setLoading] = useState(false);
    const [lastId, setLastId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const PAGE_SIZE = 50;
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let url = buildUrl('/registrations', filters, PAGE_SIZE, lastId);
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            const items = response.data.items;
            setRegistrations(prevItems => (
                lastId ? [...prevItems, ...items] : items
            ));
            setTotal(response.data.total)
            setHasMore(response.data.items.length === PAGE_SIZE);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, auth.accessToken, lastId, filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const lastItemRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setLastId(node.id);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const updateFilters = (evt) => {
        const {name, value} = evt.target;
        setFilters(curr => ({...curr, [name]: value}));
        setLastId(null);
        setRegistrations([]);
    }

    const downloadExcel = async () => {
        try {
            let url = buildUrl(
                '/registrations',
                {...filters, format: "csv"},
                PAGE_SIZE,
                lastId
            );
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            const href = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", 'registrations.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch(err) {
            console.log(err);
        }
    };

    return (
        <div className="w-full">
            <div className="rounded-lg border border-gray-200 bg-slate-100 p-6 sticky top-6">
                <p className="text-sm text-gray-500"></p>
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-medium text-gray-800">Registrations</h3>
                            <button
                                onClick={downloadExcel}
                                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                            >
                                Download Excel
                            </button>
                            <div className="flex flex-wrap gap-4 mt-2">
                                <div className="w-full md:w-auto">
                                    <label className="block text-sm font-medium text-gray-700">Search Title</label>
                                    <select
                                        name="title"
                                        className="mt-1 p-2 border border-gray-300 rounded-md"
                                        value={filters.title}
                                        onChange={updateFilters}
                                    >
                                        <option value="">-- Select title --</option>
                                        <option value="Mr.">Mr.</option>
                                        <option value="Mrs.">Mrs.</option>
                                        <option value="Miss">Miss</option>
                                        <option value="Ms.">Ms.</option>
                                        <option value="Dr.">Dr.</option>
                                        <option value="Prof.">Prof.</option>
                                        <option value="Eng.">Eng.</option>
                                        <option value="Adv.">Adv.</option>
                                        <option value="Arch.">Arch.</option>
                                        <option value="Capt.">Capt.</option>
                                        <option value="Hon.">Hon.</option>
                                        <option value="Lt.">Lt.</option>
                                        <option value="Col.">Col.</option>
                                        <option value="Gen.">Gen.</option>
                                        <option value="Maj.">Maj.</option>
                                        <option value="Sgt.">Sgt.</option>
                                        <option value="Rev.">Rev.</option>
                                        <option value="Fr.">Fr.</option>
                                        <option value="Imam">Imam</option>
                                        <option value="Rabbi">Rabbi</option>
                                        <option value="Pastor">Pastor</option>
                                        <option value="Sir">Sir</option>
                                        <option value="Dame">Dame</option>
                                        <option value="Lord">Lord</option>
                                        <option value="Lady">Lady</option>
                                    </select>
                                </div>
                                <div className="w-full md:w-auto">
                                    <label className="block text-sm font-medium text-gray-700">Search Status</label>
                                    <select
                                        name="status"
                                        className="mt-1 p-2 border border-gray-300 rounded-md"
                                        value={filters.status}
                                        onChange={updateFilters}
                                    >
                                        <option value="">-- Select Status --</option>
                                        <option value="success">success</option>
                                        <option value="failed">failed</option>
                                    </select>
                                </div>
                                <div className="w-full md:w-auto">
                                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                        value={filters.first_name}
                                        onChange={updateFilters}
                                    />
                                </div>
                                <div className="w-full md:w-auto">
                                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                                    <input
                                        type="text"
                                        name="surname"
                                        className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                        value={filters.surname}
                                        onChange={updateFilters}
                                    />
                                </div>
                                <div className="w-full md:w-auto">
                                    <label className="block text-sm font-medium text-gray-700">ID Number</label>
                                    <input
                                        type="text"
                                        name="id_number"
                                        className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                        value={filters.id_number}
                                        onChange={updateFilters}
                                    />
                                </div>
                                <div className="w-full md:w-auto">
                                    <label className="block text-sm font-medium text-gray-700">Referral Code</label>
                                    <input
                                        type="text"
                                        name="referral_code"
                                        className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                        value={filters.referral_code}
                                        onChange={updateFilters}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Min Date</label>
                                    <input
                                        type="datetime-local"
                                        name="min_date"
                                        className="mt-1 p-2 border border-gray-300 rounded-md"
                                        value={filters.min_date}
                                        onChange={updateFilters}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Max Date</label>
                                    <input
                                        type="datetime-local"
                                        name="max_date"
                                        className="mt-1 p-2 border border-gray-300 rounded-md"
                                        value={filters.max_date}
                                        onChange={updateFilters}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    Showing {registrations.length} of {total}
                    {registrations && registrations.length > 0 ? (
                        <div className="overflow-hidden">
                            <div className="max-h-80 overflow-y-auto">
                                <table className="min-w-full">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                #
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Created_At
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Referral_Code
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Voucher_Code
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Title
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Initials
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                First_Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Surname
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Id_Number
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Gender
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date_Of_Birth
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Cell_Number
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Wa_Id
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Address
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Product
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {registrations.map((payment, index) => {
                                            if (registrations.length === index + 1) {
                                                return (
                                                    <tr id={payment.id} ref={lastItemRef} key={index} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {index + 1}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.created_at}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.referral_code}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.voucher}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.title}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.initials}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.first_name}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.surname}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.id_number}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.gender}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.date_of_birth}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatForDisplay(payment.cell_number)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatForDisplay(payment.wa_id)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.address}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.product}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.status}
                                                        </td>
                                                    </tr>
                                                )
                                            } else {
                                                return (
                                                    <tr id={payment.id} key={index} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {index + 1}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.created_at}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.referral_code}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.voucher}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.title}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.initials}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.first_name}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {payment.surname}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.id_number}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.gender}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.date_of_birth}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatForDisplay(payment.cell_number)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatForDisplay(payment.wa_id)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.address}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.product}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {payment.status}
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">No payments found</div>
                    )}
                </div>
                <button onClick={() => navigate("/dashboard")} className="mt-4 inline-block text-blue-600 hover:underline">
                    ← Back to Dashboard
                </button>
            </div>
        </div>
    )
}

export default Registrations;